// src/components/PaymentSuccess.js
import React, { useEffect, useState, useContext } from 'react';
import { Container, Typography, CircularProgress, Button } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../context/UserContext';
import { verifyPaymentBySession } from '../services/api';

function PaymentSuccess() {
  const [isVerifying, setIsVerifying] = useState(true);
  const [isPaid, setIsPaid] = useState(false);
  const [error, setError] = useState(null);
  const { userGuid } = useContext(UserContext);  // Get from context if available
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        // Get session_id from URL query params
        const queryParams = new URLSearchParams(location.search);
        const sessionId = queryParams.get('session_id');
        const userGuidFromUrl = queryParams.get('user_guid');
        console.log('sessionId from stripe: ', sessionId);
        console.log('userGuid from URL:', userGuidFromUrl);
        
        if (!sessionId) {
          setError('No session ID found in the URL');
          setIsVerifying(false);
          return;
        }
         // Use user_guid from URL if available, otherwise use from context
         const effectiveUserGuid = userGuidFromUrl || userGuid;
        
         if (!effectiveUserGuid) {
           setError('Unable to determine user ID');
           setIsVerifying(false);
           return;
         }
        // Verify payment using session ID
        const paymentVerified = await verifyPaymentBySession(sessionId, effectiveUserGuid);
        setIsPaid(paymentVerified);
        
        if (!paymentVerified) {
          setError('Payment verification failed');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setError(error.message || 'An error occurred while verifying payment');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyPayment();
  }, [location.search, userGuid]);

// Handle navigation to profile
const handleGoToProfile = () => {
  // If we have a user_guid from URL but not from context, we might need to redirect to login
  const queryParams = new URLSearchParams(location.search);
  const userGuidFromUrl = queryParams.get('user_guid');
  
  if (userGuidFromUrl && !userGuid) {
    // User session was lost, redirect to login with a message
    navigate('/login', { 
      state: { 
        redirectAfterLogin: '/profile',
        message: 'Your payment was successful! Please log in to access your account.'
      } 
    });
  } else {
    // Normal navigation to profile
    navigate('/profile');
  }
};

  return (
    <Container maxWidth="sm" sx={{ mt: 5, textAlign: 'center' }}>
      {isVerifying ? (
        <CircularProgress sx={{ mt: 4 }} />
      ) : isPaid ? (
        <>
          <CheckCircle color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Payment Successful!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4 }}>
            Thank you for your payment. You now have full access to upload and manage your POLST documents.
          </Typography>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/profile')}
          >
            Go to Profile
          </Button>
        </>
      ) : (
        <>
          <Typography variant="h4" gutterBottom>
            {error ? 'Payment Verification Failed' : 'Verifying Payment...'}
          </Typography>
          
          {error ? (
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : (
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your payment is being processed. This may take a moment.
            </Typography>
          )}
          
          <Typography variant="body2" color="text.secondary">
            If your payment was successful but not showing up, please refresh this page or contact support.
          </Typography>
          
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => navigate('/profile')}
            sx={{ mt: 3 }}
          >
            Return to Profile
          </Button>
        </>
      )}
    </Container>
  );
}

export default PaymentSuccess;